import React from 'react'
import { StyleSheet, TouchableOpacity, View, Image } from 'react-native'
import Modal from 'react-native-modal'
import { useTheme } from '@react-navigation/native'
import { Theme } from '@theme'
import { Text, Button, Icon } from '@components'
import { useScreen } from '@hooks'
import { useLanguage } from '@context'

type FriendRequestModalProps = {
  isVisible: boolean
  setIsVisible: React.Dispatch<React.SetStateAction<boolean>>
  avatar: any
  name: string
  onAcceptPro: () => void
  onAcceptPrivate: () => void
  onReject: () => void
}

export const FriendRequestModal = ({
  isVisible,
  setIsVisible,
  avatar,
  name,
  onAcceptPro,
  onAcceptPrivate,
  onReject,
}: FriendRequestModalProps): React.ReactElement => {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyle(theme)
  const {isMobile} = useScreen()

  const handleAcceptPro = () => {
    onAcceptPro()
    setIsVisible(false)
  }

  const handleAcceptPrivate = () => {
    onAcceptPrivate()
    setIsVisible(false)
  }

  const handleReject = () => {
    onReject()
    setIsVisible(false)
  }

  return (
    <Modal
      isVisible={isVisible}
      onBackdropPress={() => setIsVisible(false)}
      onBackButtonPress={() => setIsVisible(false)}
      useNativeDriverForBackdrop
      style={styles.modal}
    >
      <View style={[styles.container, !isMobile && {width: 500}]}>
        {/* Bouton fermer */}
        <TouchableOpacity 
          style={styles.closeButton}
          onPress={() => setIsVisible(false)}
        >
          <Icon name="cross" size={24} />
        </TouchableOpacity>

        {/* Avatar */}
        <View style={styles.avatarContainer}>
          <Image source={avatar} style={styles.avatar} />
        </View>

        {/* Nom */}
        <Text type="titleBold" style={styles.name}>
          {name}
        </Text>

        {/* Message */}
        <Text type="body" style={styles.message}>
          {t('notifications.friendRequest')}
        </Text>

        {/* Boutons d'action */}
        <View style={styles.buttonsContainer}>
          <Button
            title={t('notifications.acceptAsPro')}
            onPress={handleAcceptPro}
            unfilled={true}
            style={styles.primaryButton}
          />
          
          <Button
            title={t('notifications.acceptAsPrivate')}
            onPress={handleAcceptPrivate}
            unfilled={true}
            isGreen={true}
            style={styles.secondaryButton}
          />
        </View>

        {/* Lien refuser */}
        <TouchableOpacity onPress={handleReject} style={styles.rejectLink}>
          <Text type="body" style={styles.rejectText}>
            {t('notifications.orRejectRequest')}
          </Text>
        </TouchableOpacity>
      </View>
    </Modal>
  )
}

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    modal: {
      justifyContent: 'center',
      alignItems: 'center',
      margin: 0,
    },
    container: {
      width: '85%',
      backgroundColor: theme.colors.white,
      borderRadius: theme.border.large,
      alignItems: 'center',
      paddingVertical: theme.spacing.xxl,
      paddingHorizontal: theme.spacing.xl,
      position: 'relative',
    },
    closeButton: {
      position: 'absolute',
      top: theme.spacing.md,
      right: theme.spacing.md,
      padding: theme.spacing.sm,
      zIndex: 1,
    },
    avatarContainer: {
      marginBottom: theme.spacing.lg,
      marginTop: theme.spacing.md,
    },
    avatar: {
      width: 80,
      height: 80,
      borderRadius: 40,
    },
    name: {
      marginBottom: theme.spacing.sm,
      textAlign: 'center',
    },
    message: {
      marginBottom: theme.spacing.xl,
      textAlign: 'center',
      color: theme.colors.greyBlue,
    },
    buttonsContainer: {
      width: '100%',
      gap: theme.spacing.md,
      marginBottom: theme.spacing.lg,
    },
    primaryButton: {
      width: '100%',
    },
    secondaryButton: {
      width: '100%',
      borderColor: theme.colors.green,
    },
    rejectLink: {
      paddingVertical: theme.spacing.sm,
    },
    rejectText: {
      color: theme.colors.greyBlue,
      textDecorationLine: 'underline',
    },
  }) 